// /opt/isp_erp/frontend/src/components/tickets/NapBuscador.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios from '../../api/axios';

const distanciaTexto = (km) => {
  if (km === null || km === undefined) return '';
  return km < 1 ? `a ${Math.round(km * 1000)} m` : `a ${Number(km).toFixed(1)} km`;
};

/**
 * Buscador de NAP para el cierre de una instalación.
 * onSelect(nap | null) recibe la NAP elegida (id, codigo, nombre, puertos_libres, porcentaje_ocupacion, ...).
 * origen = { lat, lng } con el GPS del técnico, si lo hay.
 */
const NapBuscador = ({ ticketId, value, onSelect, origen = null, disabled = false, minimoCaracteres = 0 }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState(null);
  const contador = useRef(0);
  const lat = origen ? origen.lat : null;
  const lng = origen ? origen.lng : null;

  // Si la NAP se elige por fuera (chips de cercanas), se trae su información para mostrarla
  useEffect(() => {
    if (!value) {
      setSeleccion(null);
      return undefined;
    }
    if (seleccion && seleccion.id === value) return undefined;
    let vigente = true;
    axios.get(`/tickets/${ticketId}/naps-disponibles`, { params: { nap_id: value } })
      .then((r) => { if (vigente && r.data.items && r.data.items[0]) setSeleccion(r.data.items[0]); })
      .catch(() => {});
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ticketId]);

  useEffect(() => {
    if (!abierto || !ticketId) return undefined;
    // Con minimoCaracteres > 0 no se lista nada hasta que se escriba: solo se muestran resultados de la búsqueda
    if (texto.trim().length < minimoCaracteres) {
      contador.current += 1;
      setItems([]);
      setTotal(0);
      setCargando(false);
      return undefined;
    }
    const id = ++contador.current;
    const espera = setTimeout(async () => {
      setCargando(true);
      try {
        const params = { limite: 40 };
        if (texto.trim()) params.q = texto.trim();
        if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
          params.lat = lat;
          params.lng = lng;
        }
        const r = await axios.get(`/tickets/${ticketId}/naps-disponibles`, { params });
        if (id === contador.current) {
          setItems(r.data.items || []);
          setTotal(r.data.total || 0);
        }
      } catch (e) {
        if (id === contador.current) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (id === contador.current) setCargando(false);
      }
    }, 300);
    return () => clearTimeout(espera);
  }, [abierto, texto, ticketId, lat, lng, minimoCaracteres]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      open={abierto}
      onOpen={() => setAbierto(true)}
      onClose={() => setAbierto(false)}
      options={items}
      value={seleccion}
      loading={cargando}
      filterOptions={(opciones) => opciones}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionDisabled={(o) => !o.disponible}
      getOptionLabel={(o) => (o ? `${o.codigo} - ${o.nombre}` : '')}
      onInputChange={(_, v, motivo) => {
        if (motivo === 'input') setTexto(v);
        if (motivo === 'clear') setTexto('');
      }}
      onChange={(_, nueva) => {
        setSeleccion(nueva);
        onSelect(nueva);
      }}
      noOptionsText={cargando ? 'Buscando...' : (texto.trim().length < minimoCaracteres ? `Escribe al menos ${minimoCaracteres} letras o números del código o nombre` : 'Ninguna NAP coincide')}
      renderOption={(props, o) => {
        const { key, ...resto } = props;
        return (
          <li key={o.id} {...resto}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{o.codigo} - {o.nombre}</Typography>
              <Typography variant="caption" color={o.disponible ? 'text.secondary' : 'error'}>
                {[o.zona, o.sector].filter(Boolean).join(' · ')}
                {(o.zona || o.sector) ? ' · ' : ''}
                {o.disponible ? `${o.puertos_libres} libres de ${o.capacidad_puertos}` : o.motivo}
                {o.distancia_km !== null && o.distancia_km !== undefined ? ` · ${distanciaTexto(o.distancia_km)}` : ''}
                {o.aviso ? ` · ${o.aviso}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Caja NAP (escribe para buscar)"
          helperText={abierto && total ? `${items.length} de ${total} · las más cercanas primero` : ' '}
          FormHelperTextProps={{ sx: { fontSize: '0.7rem', mt: 0.3 } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {cargando ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default NapBuscador;
